// PurchaseOrderPayments.jsx

import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';

// Utilities
import { formatPrice } from 'utilities/Currency';

// Local
import {
  createPurchaseOrderPayment,
  PURCHASE_ORDER_PAYMENT_TYPE_KEY,
} from 'nodetypes/purchaseorder/view/purchaseOrderPaymentFromPo';
import {
  computePurchaseOrderItemsRollups,
} from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';
import {
  getPurchaseOrderPaymentRowModel,
  readPurchaseOrderPaymentField,
} from 'nodetypes/purchaseorder/view/purchaseOrderPaymentsModel';
import usePurchaseOrderRelatedNodes from 'nodetypes/purchaseorder/view/usePurchaseOrderRelatedNodes';
import { PURCHASE_ORDER_ITEM_TYPE_KEY } from 'nodetypes/purchaseorderitem/purchaseOrderItemKeys';

// CSS
import './PurchaseOrderPayments.css';

// Components
import Button from 'components/button/Button';
import EnumPillSelect from 'components/enumpillselect/EnumPillSelect';
import IconButton from 'components/iconbutton/IconButton';

function cleanString(value) {
  return String(value || '').trim();
}

function formatMoneyCell(value) {
  if (value === null || value === undefined || value === '') {
    return <span className="purchase-order-payments-muted">—</span>;
  }
  return formatPrice(value);
}

function getPaymentSchemaField(paymentType, fieldName) {
  const fields = Array.isArray(paymentType?.schema?.fields) ? paymentType.schema.fields : [];
  return fields.find((field) => cleanString(field?.name) === fieldName) || null;
}

/**
 * PurchaseOrderPayments
 *
 * Payment schedule / ledger for PURCHASE_ORDER (display + status edit + schedule create).
 */
const PurchaseOrderPayments = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  neighborNodes: neighborNodesProp = null,
  onRelatedNodeOpen = null,
}) => {
  const { activeBase } = useBase();
  const { user } = useUser();
  const { getType } = useNodeTypes();
  const language = useCurrentLanguage();

  const [scheduleBusy, setScheduleBusy] = useState(false);

  const baseKey = cleanString(activeBase?.key || node?.baseKey);
  const purchaseOrderKey = cleanString(node?.key);
  const canEdit = Boolean(!readOnly);
  const canOpenPayment = typeof onRelatedNodeOpen === 'function';
  const subscribedNeighbors = usePurchaseOrderRelatedNodes(
    neighborNodesProp == null ? purchaseOrderKey : null,
    neighborNodesProp == null ? baseKey : null,
  );
  const neighborNodes = neighborNodesProp == null ? subscribedNeighbors : neighborNodesProp;

  const paymentType = useMemo(() => getType(PURCHASE_ORDER_PAYMENT_TYPE_KEY), [getType]);
  const statusField = useMemo(
    () => getPaymentSchemaField(paymentType, 'status'),
    [paymentType],
  );

  const payments = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === PURCHASE_ORDER_PAYMENT_TYPE_KEY)
      .sort((left, right) => {
        const leftOrder = Number(readPurchaseOrderPaymentField(left, 'sortOrder'));
        const rightOrder = Number(readPurchaseOrderPaymentField(right, 'sortOrder'));
        if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return cleanString(left?.title).localeCompare(cleanString(right?.title));
      })
  ), [neighborNodes]);

  const items = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === PURCHASE_ORDER_ITEM_TYPE_KEY)
  ), [neighborNodes]);

  const rows = useMemo(
    () => payments.map((payment) => ({
      payment,
      model: getPurchaseOrderPaymentRowModel(payment, paymentType, language),
    })),
    [language, paymentType, payments],
  );

  const rollups = useMemo(
    () => computePurchaseOrderItemsRollups(items, node, nodeType, payments),
    [items, node, nodeType, payments],
  );

  const handlePaymentChange = useCallback((paymentKey, patch) => {
    if (!canEdit || !paymentKey || !patch) return undefined;
    return updateNode(paymentKey, patch);
  }, [canEdit]);

  const handleRowOpen = useCallback((payment) => {
    if (!canOpenPayment || !payment?.key) return;
    onRelatedNodeOpen(payment);
  }, [canOpenPayment, onRelatedNodeOpen]);

  /**
   * Creates a scheduled payment edged to this PO, optionally opening Peek.
   *
   * @returns {Promise<void>}
   */
  const handleSchedulePayment = useCallback(async () => {
    if (!canEdit || scheduleBusy || !node?.key || !user?.key) return;

    setScheduleBusy(true);
    try {
      const remaining = Math.max(0, Number(rollups.balanceDue) || 0);
      const created = await createPurchaseOrderPayment({
        purchaseOrderNode: node,
        user,
        baseKey: baseKey || null,
        paymentType,
        purchaseOrderType: nodeType,
        existingCount: payments.length,
        defaultAmountCents: remaining > 0 ? remaining : null,
      });
      if (canOpenPayment && created?.key) {
        onRelatedNodeOpen(created);
      }
    } catch (error) {
      toast.error(error?.message || 'Could not schedule a payment.', {
        autoClose: 4000,
      });
    } finally {
      setScheduleBusy(false);
    }
  }, [
    baseKey,
    canEdit,
    canOpenPayment,
    node,
    nodeType,
    onRelatedNodeOpen,
    paymentType,
    payments.length,
    rollups.balanceDue,
    scheduleBusy,
    user,
  ]);

  if (!node) return null;

  const tableClassName = [
    'purchase-order-payments-table',
    canOpenPayment ? 'has-row-actions' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="purchase-order-payments">
      <div className="purchase-order-payments-header">
        <div className="purchase-order-payments-title">
          {`Payments (${rows.length})`}
        </div>

        <div className="purchase-order-payments-header-actions">
          {canEdit ? (
            <Button
              label={scheduleBusy ? 'Scheduling…' : 'Schedule a Payment'}
              iconKey="add"
              height="30px"
              enabled={!scheduleBusy}
              onClick={handleSchedulePayment}
            />
          ) : null}
        </div>
      </div>

      <div className="purchase-order-payments-body">
        {rows.length === 0 ? (
          <div className="purchase-order-payments-empty">
            No payments scheduled.
            {canEdit ? ' Schedule installments against this order.' : ''}
          </div>
        ) : (
          <div className="purchase-order-payments-scroll">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th className="purchase-order-payments-col-name" scope="col">Payment</th>
                  <th className="purchase-order-payments-col-amount" scope="col">Amount</th>
                  <th className="purchase-order-payments-col-terms" scope="col">Terms</th>
                  <th className="purchase-order-payments-col-due" scope="col">Due</th>
                  <th className="purchase-order-payments-col-status" scope="col">Status</th>
                  {canOpenPayment ? (
                    <th className="purchase-order-payments-col-actions" scope="col">
                      <span className="purchase-order-payments-sr-only">Open</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ payment, model }) => (
                  <tr key={model.key}>
                    <td className="purchase-order-payments-col-name">
                      <span className="purchase-order-payments-name">
                        {model.title}
                      </span>
                    </td>
                    <td className="purchase-order-payments-col-amount">
                      {formatMoneyCell(model.amount)}
                    </td>
                    <td className="purchase-order-payments-col-terms">
                      {model.paymentTermsLabel || (
                        <span className="purchase-order-payments-muted">—</span>
                      )}
                    </td>
                    <td className="purchase-order-payments-col-due">
                      {model.dueDateLabel || (
                        <span className="purchase-order-payments-muted">—</span>
                      )}
                    </td>
                    <td className="purchase-order-payments-col-status">
                      <div className="purchase-order-payments-status">
                        {statusField ? (
                          <EnumPillSelect
                            field={statusField}
                            value={model.status}
                            node={payment}
                            typeKey={PURCHASE_ORDER_PAYMENT_TYPE_KEY}
                            canEdit={canEdit}
                            onNodeChange={handlePaymentChange}
                            placeholder="Status…"
                            language={language}
                            fontSize="12px"
                          />
                        ) : (
                          model.statusLabel || (
                            <span className="purchase-order-payments-muted">—</span>
                          )
                        )}
                      </div>
                    </td>
                    {canOpenPayment ? (
                      <td className="purchase-order-payments-col-actions">
                        <div className="purchase-order-payments-row-action">
                          <IconButton
                            iconKey="more_horiz"
                            title="Open payment"
                            size={28}
                            iconSize={18}
                            onClick={() => handleRowOpen(payment)}
                          />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
});

PurchaseOrderPayments.displayName = 'PurchaseOrderPayments';

export default PurchaseOrderPayments;
